import { useEffect, useMemo, useRef, useState } from "react";
import {
  Check,
  ChevronDown,
  Monitor,
  Image as ImageIcon,
  RotateCcw,
  Save,
} from "lucide-react";
import { toast } from "sonner";
import ImagePicker from "./ImagePicker";
import { useSiteSettings } from "../../context/SiteSettingsContext";
import {
  DEFAULT_THEME_PALETTE_ID,
  getThemePalette,
  THEME_PALETTES,
} from "../../lib/themePalettes";

function stableVisualSignature(visual) {
  if (!visual) return "";
  return JSON.stringify({
    theme_palette: visual.theme_palette || "",
    theme: visual.theme || {},
    logos: visual.logos || {},
    favicon: visual.favicon || {},
  });
}

function ColorField({ label, value, onChange, testid }) {
  return (
    <div className="space-y-2">
      <label className="label-x">{label}</label>
      <div className="flex items-center gap-3">
        <input
          type="color"
          value={value || "#000000"}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          className="h-11 w-14 rounded-xl border border-[var(--border-light)] bg-white cursor-pointer"
          data-testid={`${testid}-picker`}
        />
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          className="input-x font-mono-tab uppercase"
          placeholder="#FFFFFF"
          data-testid={testid}
        />
      </div>
    </div>
  );
}

function NumberField({ label, value, onChange, min = 24, max = 200, testid }) {
  return (
    <div className="space-y-2">
      <label className="label-x">{label}</label>
      <div className="flex items-center gap-3">
        <input
          type="range"
          min={min}
          max={max}
          value={value || min}
          onChange={(e) => onChange(Number(e.target.value))}
          className="flex-1"
          data-testid={`${testid}-range`}
        />
        <input
          type="number"
          min={min}
          max={max}
          value={value || min}
          onChange={(e) => onChange(Number(e.target.value))}
          className="input-x w-28"
          data-testid={testid}
        />
      </div>
    </div>
  );
}

function PaletteThumbnail({ palette }) {
  const colors = [
    palette.theme.accent,
    palette.theme.accent_hover,
    palette.theme.bg_primary,
    palette.theme.interactive_bg,
    palette.theme.text_primary,
  ];

  return (
    <div className="flex items-center gap-1" aria-hidden="true">
      {colors.map((color, index) => (
        <span
          key={`${palette.id}-${index}`}
          className="h-4 w-4 rounded-full border border-black/10 shrink-0"
          style={{ backgroundColor: color }}
        />
      ))}
    </div>
  );
}

function PalettePicker({ selectedId, onChange }) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const selectedPalette = getThemePalette(selectedId || DEFAULT_THEME_PALETTE_ID);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (!wrapperRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const selectPalette = (paletteId) => {
    onChange(paletteId);
    setOpen(false);
  };

  return (
    <div className="space-y-3" ref={wrapperRef}>
      <label className="label-x">Theme Palette</label>
      <div className="relative">
        <button
          type="button"
          className="w-full rounded-2xl border border-[var(--border-light)] bg-white px-4 py-3 text-left flex items-center justify-between gap-4 shadow-sm"
          onClick={() => setOpen((current) => !current)}
          aria-expanded={open}
          data-testid="theme-palette-trigger"
        >
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-3">
              <PaletteThumbnail palette={selectedPalette} />
              <span className="font-semibold text-[var(--text-primary)]">{selectedPalette.label}</span>
            </div>
            <p className="text-sm text-[var(--text-secondary)] mt-2 line-clamp-2">{selectedPalette.description}</p>
          </div>
          <ChevronDown className={`w-4 h-4 text-[var(--text-muted)] shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>

        {open ? (
          <div
            className="absolute z-20 mt-3 w-full rounded-3xl border border-[var(--border-light)] bg-white p-3 shadow-[0_18px_45px_-20px_rgba(15,23,42,0.25)]"
            data-testid="theme-palette-menu"
          >
            <div className="grid gap-2 sm:grid-cols-2">
              {THEME_PALETTES.map((palette) => {
                const active = palette.id === selectedPalette.id;
                return (
                  <button
                    key={palette.id}
                    type="button"
                    className={`rounded-2xl border p-4 text-left transition-colors ${
                      active
                        ? "border-[var(--gold)] bg-[var(--bg-secondary)]"
                        : "border-[var(--border-light)] hover:border-[var(--gold)] hover:bg-[var(--bg-secondary)]/50"
                    }`}
                    onClick={() => selectPalette(palette.id)}
                    data-testid={`theme-palette-option-${palette.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <PaletteThumbnail palette={palette} />
                      {active ? <Check className="w-4 h-4 text-[var(--gold)] shrink-0" /> : null}
                    </div>
                    <div className="mt-3 font-semibold text-[var(--text-primary)]">{palette.label}</div>
                    <p className="text-sm text-[var(--text-secondary)] mt-2">{palette.description}</p>
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default function VisualSettingsEditor() {
  const { settings, saveSettings } = useSiteSettings();
  const [draft, setDraft] = useState(settings?.visual);
  const [saving, setSaving] = useState(false);
  const draftRef = useRef(draft);
  const settingsVisualSignature = useMemo(
    () => stableVisualSignature(settings?.visual),
    [settings?.visual],
  );
  const draftVisualSignature = useMemo(
    () => stableVisualSignature(draft),
    [draft],
  );

  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);

  useEffect(() => {
    // #region debug-point A:draft-sync
    fetch("http://127.0.0.1:7777/event",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({sessionId:"theme-palette-loop",runId:"pre-fix",hypothesisId:"A",location:"VisualSettingsEditor.jsx:187",msg:"[DEBUG] syncing draft from settings",data:{incomingPalette:settings?.visual?.theme_palette||null,currentDraftPalette:draftRef.current?.theme_palette||null,incomingAccent:settings?.visual?.theme?.accent||null,ts:Date.now()}})}).catch(()=>{});
    // #endregion
    setDraft((current) => (
      stableVisualSignature(current) === settingsVisualSignature ? current : settings?.visual
    ));
  }, [settings?.visual, settingsVisualSignature]);

  const dirty = useMemo(
    () => JSON.stringify(draft || {}) !== JSON.stringify(settings?.visual || {}),
    [draft, settings?.visual],
  );

  const selectedPalette = useMemo(
    () => getThemePalette(draft?.theme_palette || DEFAULT_THEME_PALETTE_ID),
    [draft?.theme_palette],
  );

  const setThemePalette = (paletteId) => {
    const palette = getThemePalette(paletteId);
    // #region debug-point E:palette-select
    fetch("http://127.0.0.1:7777/event",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({sessionId:"theme-palette-loop",runId:"pre-fix",hypothesisId:"E",location:"VisualSettingsEditor.jsx:210",msg:"[DEBUG] palette selected",data:{nextPalette:palette.id,nextAccent:palette.theme?.accent||null,currentDraftPalette:draft?.theme_palette||null,ts:Date.now()}})}).catch(()=>{});
    // #endregion
    setDraft((prev) => ({
      ...prev,
      theme_palette: palette.id,
      theme: {
        ...palette.theme,
      },
    }));
  };

  const setLogo = (area, key, value) => {
    setDraft((prev) => ({
      ...prev,
      logos: {
        ...(prev?.logos || {}),
        [area]: {
          ...(prev?.logos?.[area] || {}),
          [key]: value,
        },
      },
    }));
  };

  const setFavicon = (key, value) => {
    setDraft((prev) => ({
      ...prev,
      favicon: {
        ...(prev?.favicon || {}),
        [key]: value,
      },
    }));
  };

  const reset = () => {
    setDraft(settings?.visual);
  };

  const save = async () => {
    setSaving(true);
    try {
      await saveSettings({ visual: draft });
      toast.success("Visual settings saved.");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not save visual settings.");
    } finally {
      setSaving(false);
    }
  };

  if (!draft) return null;

  return (
    <div className="space-y-6" data-testid="visual-settings-editor">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="eyebrow mb-2">VISUAL SETTINGS</div>
          <h2 className="font-display text-2xl md:text-3xl font-bold text-[var(--text-primary)]">Theme, logos and favicon</h2>
          <p className="text-sm text-[var(--text-secondary)] mt-2 max-w-2xl">
            Palette changes are previewed safely inside this card and applied globally after you save.
          </p>
        </div>
        <div className="flex gap-3">
          <button type="button" className="btn-secondary" onClick={reset} data-testid="visual-settings-reset">
            <RotateCcw className="w-4 h-4" /> Reset
          </button>
          <button type="button" className="btn-primary disabled:opacity-50" disabled={!dirty || saving} onClick={save} data-testid="visual-settings-save">
            <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save Visual Settings"}
          </button>
        </div>
      </div>

      <div className="grid xl:grid-cols-[1.2fr_0.8fr] gap-6">
        <div className="bg-white rounded-3xl border border-[var(--border-light)] p-6 space-y-6">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Monitor className="w-4 h-4 text-[var(--gold)]" />
              <div className="font-display text-xl font-bold text-[var(--text-primary)]">Theme Colors</div>
            </div>
            <PalettePicker selectedId={draft?.theme_palette} onChange={setThemePalette} />
            <div className="mt-5 rounded-3xl border border-[var(--border-light)] bg-[var(--bg-secondary)]/40 p-4">
              <div className="text-xs tracking-[0.22em] font-semibold text-[var(--gold)]">SELECTED PALETTE</div>
              <div className="flex flex-wrap items-center gap-3 mt-3">
                <PaletteThumbnail palette={selectedPalette} />
                <div className="font-semibold text-[var(--text-primary)]">{selectedPalette.label}</div>
              </div>
              <p className="text-sm text-[var(--text-secondary)] mt-3">
                Picking a palette updates the entire theme token set live and keeps contrast aligned across the panel.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-3xl border border-[var(--border-light)] p-6 space-y-5">
            <div className="flex items-center gap-2">
              <ImageIcon className="w-4 h-4 text-[var(--gold)]" />
              <div className="font-display text-xl font-bold text-[var(--text-primary)]">Header Logo</div>
            </div>
            <ImagePicker value={draft?.logos?.header?.url} onChange={(value) => setLogo("header", "url", value)} aspect="4/1" />
            <NumberField label="Header Logo Height" value={draft?.logos?.header?.max_height} onChange={(value) => setLogo("header", "max_height", value)} testid="header-logo-height" />
          </div>

          <div className="bg-white rounded-3xl border border-[var(--border-light)] p-6 space-y-5">
            <div className="font-display text-xl font-bold text-[var(--text-primary)]">Footer Logo</div>
            <ImagePicker value={draft?.logos?.footer?.url} onChange={(value) => setLogo("footer", "url", value)} aspect="4/1" />
            <NumberField label="Footer Logo Height" value={draft?.logos?.footer?.max_height} onChange={(value) => setLogo("footer", "max_height", value)} testid="footer-logo-height" />
          </div>

          <div className="bg-white rounded-3xl border border-[var(--border-light)] p-6 space-y-5">
            <div className="font-display text-xl font-bold text-[var(--text-primary)]">Favicon</div>
            <ImagePicker value={draft?.favicon?.url} onChange={(value) => setFavicon("url", value)} aspect="1/1" compact />
            <ColorField label="Browser Theme Color" value={draft?.favicon?.theme_color} onChange={(value) => setFavicon("theme_color", value)} testid="favicon-theme-color" />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-[var(--border-light)] p-6">
        <div className="eyebrow mb-3">LIVE PREVIEW</div>
        <div
          className="rounded-3xl overflow-hidden border"
          style={{
            borderColor: selectedPalette.theme.border_light,
            backgroundColor: selectedPalette.theme.bg_primary,
          }}
        >
          <div
            className="px-6 py-5 border-b flex items-center justify-between gap-4"
            style={{
              backgroundColor: selectedPalette.theme.bg_primary,
              borderColor: selectedPalette.theme.border_light,
            }}
          >
            <div
              className="font-display text-2xl font-bold"
              style={{ color: selectedPalette.theme.text_primary }}
            >
              Vissera
            </div>
            <button type="button" className="btn-primary">Request a Quote</button>
          </div>
          <div
            className="px-6 py-8"
            style={{ backgroundColor: selectedPalette.theme.bg_secondary }}
          >
            <div className="eyebrow mb-3" style={{ color: selectedPalette.theme.accent }}>Preview</div>
            <div
              className="font-display text-3xl font-bold"
              style={{ color: selectedPalette.theme.text_primary }}
            >
              Theme updates appear here instantly
            </div>
            <p className="mt-3 max-w-xl" style={{ color: selectedPalette.theme.text_secondary }}>
              This panel reflects your selected palette before it is saved permanently.
            </p>
            <div className="flex flex-wrap gap-3 mt-6">
              <button
                type="button"
                className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold"
                style={{
                  backgroundColor: selectedPalette.theme.accent,
                  color: selectedPalette.theme.button_text,
                }}
              >
                Primary Button
              </button>
              <button
                type="button"
                className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold border"
                style={{
                  borderColor: selectedPalette.theme.interactive_bg,
                  color: selectedPalette.theme.interactive_bg,
                  backgroundColor: selectedPalette.theme.bg_primary,
                }}
              >
                Secondary Button
              </button>
            </div>
            <div className="mt-6 grid sm:grid-cols-3 gap-3">
              <div
                className="rounded-2xl border p-4"
                style={{
                  borderColor: selectedPalette.theme.border_light,
                  backgroundColor: selectedPalette.theme.bg_primary,
                }}
              >
                <div
                  className="text-xs uppercase tracking-[0.2em]"
                  style={{ color: selectedPalette.theme.text_muted }}
                >
                  Accent
                </div>
                <div className="mt-3 h-10 rounded-xl" style={{ backgroundColor: selectedPalette.theme.accent }} />
              </div>
              <div
                className="rounded-2xl border p-4"
                style={{
                  borderColor: selectedPalette.theme.border_light,
                  backgroundColor: selectedPalette.theme.bg_primary,
                }}
              >
                <div
                  className="text-xs uppercase tracking-[0.2em]"
                  style={{ color: selectedPalette.theme.text_muted }}
                >
                  Surface
                </div>
                <div className="mt-3 h-10 rounded-xl" style={{ backgroundColor: selectedPalette.theme.bg_secondary }} />
              </div>
              <div
                className="rounded-2xl border p-4"
                style={{
                  borderColor: selectedPalette.theme.border_light,
                  backgroundColor: selectedPalette.theme.bg_primary,
                }}
              >
                <div
                  className="text-xs uppercase tracking-[0.2em]"
                  style={{ color: selectedPalette.theme.text_muted }}
                >
                  Interactive
                </div>
                <div className="mt-3 h-10 rounded-xl" style={{ backgroundColor: selectedPalette.theme.interactive_bg }} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
